<template>
  <div>
    <UserManagedHubModal
      :value="value"
      title="我的资源"
      add-tab-label="添加资源"
      manage-tab-label="管理资源"
      :active-tab.sync="innerTab"
      :usage-text="usageText"
      @input="$emit('input', $event)"
      @visible-change="onHubVisibleChange"
    >
      <template #add>
        <AddLlmModelModal embedded :model-item="null" @confirm="handleAddConfirm" />
      </template>
      <template #manage="{ switchTab }">
        <div v-if="!modelList.length" class="vc-my-resources__empty">
          暂无自定义资源，可切换到「添加资源」创建
          <Button type="text" class="vc-my-resources__empty-link" @click="switchTab('add')">
            去添加
          </Button>
        </div>
        <div v-else class="vc-my-resources__list">
          <div
            v-for="row in modelList"
            :key="row.id"
            class="vc-my-resources__item"
          >
            <div class="vc-my-resources__item-main">
              <FileTextOutlinedIcon :size="18" class="vc-my-resources__item-icon" />
              <span class="vc-my-resources__item-name">{{ row.name || '未命名资源' }}</span>
              <Tag v-if="row.provider" class="vc-my-resources__item-tag">{{ row.provider }}</Tag>
            </div>
            <div class="vc-my-resources__item-actions">
              <EditIconButton size="md" title="编辑" @click="handleEdit(row)" />
              <DeleteIconButton size="md" title="删除" @click="handleDelete(row)" />
            </div>
          </div>
        </div>
      </template>
    </UserManagedHubModal>

    <AddLlmModelModal
      v-model="editModalVisible"
      :model-item="editingModel"
      @confirm="handleEditConfirm"
    />
  </div>
</template>

<script>
import AddLlmModelModal from './AddLlmModelModal.vue'
import UserManagedHubModal from './UserManagedHubModal.vue'
import DeleteIconButton from '../shared/DeleteIconButton.vue'
import EditIconButton from '../shared/EditIconButton.vue'
import FileTextOutlinedIcon from '../shared/FileTextOutlinedIcon.vue'
import {
  buildCustomLlmModelResourceValue,
  createCustomLlmModel,
  isCustomLlmModelResource,
  sanitizeCustomLlmModels,
  syncLlmFieldsFromResource,
} from '../../utils/tripleAccessHelpers'

export default {
  name: 'MyLlmModelsModal',
  components: {
    AddLlmModelModal,
    UserManagedHubModal,
    DeleteIconButton,
    EditIconButton,
    FileTextOutlinedIcon,
  },
  props: {
    value: { type: Boolean, default: false },
    initialTab: { type: String, default: 'add' },
    tripleAccess: { type: Object, required: true },
    maxMyModels: { type: Number, default: 5 },
  },
  data() {
    return {
      innerTab: 'add',
      editModalVisible: false,
      editingModel: null,
    }
  },
  computed: {
    modelList() {
      const llm = this.tripleAccess && this.tripleAccess.llm
      return sanitizeCustomLlmModels(llm && llm.custom_models)
    },
    usageText() {
      return `已用 ${this.modelList.length}/${this.maxMyModels}`
    },
  },
  methods: {
    onHubVisibleChange(visible) {
      if (visible) {
        this.innerTab = this.initialTab === 'manage' ? 'manage' : 'add'
      }
    },
    ensureCustomLlmModelsArray() {
      const llm = this.tripleAccess && this.tripleAccess.llm
      if (!llm || typeof llm !== 'object') return
      if (!Array.isArray(llm.custom_models)) {
        this.$set(llm, 'custom_models', sanitizeCustomLlmModels(llm.custom_models))
      }
    },
    handleAddConfirm(payload) {
      this.ensureCustomLlmModelsArray()
      const llm = this.tripleAccess.llm
      const list = sanitizeCustomLlmModels(llm.custom_models)
      if (list.length >= this.maxMyModels) {
        this.$Message.warning(`最多保存 ${this.maxMyModels} 个资源`)
        return
      }
      const modelItem = createCustomLlmModel(payload)
      llm.custom_models.push(modelItem)
      llm.resource = buildCustomLlmModelResourceValue(modelItem.id)
      syncLlmFieldsFromResource(llm)
      this.$Message.success('已添加')
      this.$emit('added', { id: modelItem.id })
      this.$emit('input', false)
    },
    handleEdit(row) {
      this.editingModel = row ? { ...row } : null
      this.editModalVisible = true
    },
    handleEditConfirm(payload) {
      const id = String(payload && payload.id != null ? payload.id : '').trim()
      if (!id) return

      const llm = this.tripleAccess && this.tripleAccess.llm
      if (!llm || !Array.isArray(llm.custom_models)) return

      const index = llm.custom_models.findIndex(
        (item) => String(item && item.id != null ? item.id : '').trim() === id,
      )
      if (index < 0) return

      const updated = {
        ...llm.custom_models[index],
        name: payload.name,
        provider: payload.provider,
        url: payload.url,
        api_key: payload.api_key,
        description: payload.description,
        model: payload.model,
        type: 'llm',
      }
      this.$set(llm.custom_models, index, updated)

      const resource = String(llm.resource != null ? llm.resource : '').trim()
      if (isCustomLlmModelResource(resource) && resource === buildCustomLlmModelResourceValue(id)) {
        syncLlmFieldsFromResource(llm)
      }

      this.editingModel = null
      this.editModalVisible = false
      this.$Message.success('已保存')
      this.$emit('updated', { id })
    },
    handleDelete(row) {
      const id = String(row && row.id != null ? row.id : '').trim()
      const name = String(row && row.name != null ? row.name : '').trim() || '该资源'
      if (!id) return

      this.$Modal.confirm({
        title: '删除资源',
        content: `确定删除「${name}」吗？删除后不可恢复。`,
        onOk: () => {
          const llm = this.tripleAccess && this.tripleAccess.llm
          if (!llm || !Array.isArray(llm.custom_models)) return

          const index = llm.custom_models.findIndex(
            (item) => String(item && item.id != null ? item.id : '').trim() === id,
          )
          if (index < 0) return

          llm.custom_models.splice(index, 1)

          const resource = String(llm.resource != null ? llm.resource : '').trim()
          if (isCustomLlmModelResource(resource) && resource === buildCustomLlmModelResourceValue(id)) {
            this.$set(llm, 'resource', '')
            this.$set(llm, 'model', '')
            this.$set(llm, 'api_key', '')
            this.$set(llm, 'base_url', '')
          }

          this.$Message.success('已删除')
          this.$emit('deleted', { id })
        },
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.vc-my-resources {
  &__empty {
    padding: 32px 16px;
    text-align: center;
    font-size: 13px;
    color: #909399;
    border: 1px dashed #e8eaec;
    border-radius: 8px;
    background: #fafafa;
    line-height: 1.6;
  }

  &__empty-link {
    padding: 0 4px !important;
    color: #5668f0 !important;
    font-size: 13px !important;
    height: auto !important;
    vertical-align: baseline;
  }

  &__list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    padding: 14px 16px;
    border: 1px solid #e8eaec;
    border-radius: 8px;
    background: #fff;
  }

  &__item-main {
    display: flex;
    align-items: center;
    gap: 8px;
    min-width: 0;
    flex: 1;
  }

  &__item-icon {
    flex-shrink: 0;
    color: #a5a5a5;
  }

  &__item-name {
    font-size: 14px;
    font-weight: 600;
    color: #262626;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  &__item-tag {
    flex-shrink: 0;
    margin: 0 !important;
    border: none !important;
    background: #f4f4f5 !important;
    color: #909399 !important;
    font-size: 12px !important;
    line-height: 20px !important;
    height: 22px !important;
    padding: 0 8px !important;
  }

  &__item-actions {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    flex-shrink: 0;
  }
}
</style>
